"use client";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { clinicAPI, handleApiError } from "../../../services/api";
import { showSuccessToast, showErrorToast } from "../../../utils/notificationUtils";

/**
 * Clinic-facing "Connect E-Invoicing" modal (Setup Dashboard → clinic card).
 *
 * The clinic owner pastes the credentials their tax authority issued them
 * (Jordan: JoFotara Client ID + Secret Key; Saudi: VAT number + seller
 * address — CSIDs arrive with ZATCA onboarding later). Saving updates the
 * clinic's invoice profile; an admin still flips the final enable switch.
 *
 * The button that opens this modal only appears for verified, paid-plan
 * clinics — the API enforces the same gate.
 */
const FIELD_SETS = {
  JO: [
    { key: "client_id", label: "JoFotara Client ID" },
    { key: "secret_key", label: "JoFotara Secret Key", secret: true },
    { key: "tax_number", label: "Tax / National Number" },
    { key: "seller_street", label: "Seller Street Name" },
    { key: "seller_building_number", label: "Seller Building Number" },
    { key: "seller_city", label: "Seller City" },
    { key: "seller_governorate", label: "Seller Governorate" },
    { key: "seller_postal_code", label: "Seller Postal Code" },
  ],
  SA: [
    { key: "cr_number", label: "Commercial Registration (CRN)" },
    { key: "vat_number", label: "VAT Registration Number (15 digits)" },
    { key: "seller_street", label: "Seller Street Name" },
    { key: "seller_building_number", label: "Seller Building Number" },
    { key: "seller_district", label: "Seller District / Neighborhood" },
    { key: "seller_city", label: "Seller City" },
    { key: "seller_province", label: "Seller Province / Region" },
    { key: "seller_postal_code", label: "Seller Postal Code" },
  ],
};

const HELP_LINKS = {
  JO: "https://istd.gov.jo",
  SA: "https://zatca.gov.sa",
};

const ConnectEInvoicingModal = ({ isOpen, onClose, clinic }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (!isOpen || !clinic?.id) return;

    setLoading(true);
    setError(null);
    setData(null);
    setDraft({});

    clinicAPI
      .getEInvoiceSettings(clinic.id)
      .then((res) => {
        const d = res?.data?.data || {};
        setData(d);
        setDraft(d.country_settings || {});
      })
      .catch((err) => setError(handleApiError(err)))
      .finally(() => setLoading(false));
  }, [isOpen, clinic?.id]);

  if (!isOpen) return null;

  const country = data?.country_code || "";
  const fields = FIELD_SETS[country] || [];
  const readiness = data?.readiness;
  const helpLink = HELP_LINKS[country];

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await clinicAPI.updateEInvoiceSettings(clinic.id, draft);
      const d = res?.data?.data || {};
      setData((prev) => ({ ...(prev || {}), ...d }));
      showSuccessToast(
        t("einvoiceOnboarding.saved", "E-invoicing settings saved.")
      );
    } catch (err) {
      const msg = handleApiError(err);
      setError(msg);
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #E5E7EB",
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "inherit",
  };

  return (
    <div className="reception-modal-overlay" dir={i18n.dir()} lang={i18n.language}>
      <div className="reception-modal" style={{ maxWidth: 560, width: "95%" }}>
        <div className="reception-modal-header" style={{ flexDirection: isRTL ? "row-reverse" : "row" }}>
          <h2 className="reception-modal-title">
            {t("einvoiceOnboarding.title", "Connect E-Invoicing")}
          </h2>
        </div>

        <div
          className="reception-modal-content"
          style={{ direction: isRTL ? "rtl" : "ltr", textAlign: isRTL ? "right" : "left" }}
        >
          {loading && (
            <p style={{ color: "#6B7280", fontSize: 13 }}>{t("common.loading", "Loading...")}</p>
          )}

          {error && !loading && (
            <div className="invoice-manager-error" style={{ marginBottom: 12 }}>{error}</div>
          )}

          {!loading && data && !fields.length && (
            <p style={{ color: "#6B7280", fontSize: 13 }}>
              {t(
                "einvoiceOnboarding.unsupportedCountry",
                "E-invoicing is not available in your clinic's country yet."
              )}
            </p>
          )}

          {!loading && fields.length > 0 && (
            <>
              {data?.enabled && (
                <div
                  style={{
                    background: "#d1fae5",
                    color: "#065f46",
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 14,
                  }}
                >
                  {t("einvoiceOnboarding.active", "E-invoicing is active for your clinic.")}
                </div>
              )}

              {helpLink && (
                <p style={{ fontSize: 13, color: "#4B5563", marginBottom: 14 }}>
                  {t(
                    "einvoiceOnboarding.noKeys",
                    "Don't have your keys yet? Generate them on your tax authority's portal, then paste them here."
                  )}{" "}
                  <a href={helpLink} target="_blank" rel="noreferrer" style={{ color: "#8548A8", fontWeight: 600 }}>
                    {t("einvoiceOnboarding.getKeys", "Get your keys")}
                  </a>
                </p>
              )}

              <div className="connect-einv-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 14px" }}>
                {fields.map((f) => (
                  <div key={f.key}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>
                      {t(`einvoiceOnboarding.fields.${f.key}`, f.label)}
                    </label>
                    <input
                      type={f.secret ? "password" : "text"}
                      value={draft[f.key] || ""}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      autoComplete="off"
                      {...(f.secret ? { autoComplete: "new-password" } : {})}
                      style={inputStyle}
                      placeholder={t("einvoiceOnboarding.pending", "Pending")}
                    />
                  </div>
                ))}
              </div>

              {readiness && (
                <div
                  style={{
                    marginTop: 14,
                    fontSize: 12.5,
                    borderRadius: 8,
                    padding: "10px 12px",
                    background: readiness.ready ? "#d1fae5" : "#fffbeb",
                    color: readiness.ready ? "#065f46" : "#92400e",
                    border: `1px solid ${readiness.ready ? "#a7f3d0" : "#fde68a"}`,
                  }}
                >
                  {readiness.ready ? (
                    <>
                      <strong>{t("einvoiceOnboarding.ready", "Everything's set.")}</strong>{" "}
                      {t(
                        "einvoiceOnboarding.readyHint",
                        "Your Sulhafa admin can now activate e-invoicing for your clinic."
                      )}
                    </>
                  ) : (
                    <>
                      <strong>{t("einvoiceOnboarding.stillMissing", "Still missing")}:</strong>{" "}
                      {readiness.missing.join(" · ")}
                    </>
                  )}
                </div>
              )}

              <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 12 }}>
                {t(
                  "einvoiceOnboarding.adminNote",
                  "Activation is completed by Sulhafa's team after reviewing your details — you can keep issuing normal invoices in the meantime."
                )}
              </p>
            </>
          )}
        </div>

        <div className="reception-modal-actions" style={{ flexDirection: isRTL ? "row-reverse" : "row" }}>
          <button
            type="button"
            className="reception-action-btn"
            onClick={onClose}
            style={{
              background: "#fff",
              color: "#374151",
              border: "2px solid #E5E7EB",
            }}
          >
            {t("common.close", "Close")}
          </button>
          {fields.length > 0 && (
            <button
              type="button"
              className="reception-action-btn"
              disabled={saving || loading}
              onClick={handleSave}
              style={{
                background: saving ? "#c4b5fd" : "linear-gradient(135deg, #8548A8, #6d3590)",
                color: "#fff",
                opacity: saving || loading ? 0.7 : 1,
                cursor: saving || loading ? "not-allowed" : "pointer",
              }}
            >
              {saving
                ? t("common.saving", "Saving...")
                : t("common.save", "Save")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectEInvoicingModal;
